/**
 * What the organizations changed, as a table the instructor reads row by row.
 *
 * One component for three places (I6, decision D9): the Updates tab at the top
 * of Programs, which spans every program they teach in; the Updates tab inside
 * one program, narrowed by `programId`; and the Updates tab on one class,
 * narrowed by `classId`. Each row opens the class it describes (or the program
 * when it has no class) and is marked read by that click alone — reading one
 * clears only that one. Mark all as read is the broom for the rest.
 *
 * **The columns follow where you are.** Across every program a row needs to
 * say which program and which class it is about; inside one program the
 * program column is the page's own title, and inside one class both are, so
 * each is dropped rather than repeating the heading on every row. Who sent it
 * is always shown: the admin whose action it was, and their organization.
 *
 * `onUnread(n)` reports the unread count after every load and every change, so
 * the tab the list lives under can carry the same digit; the nav badge is
 * re-read from /auth/me after each change for the same reason.
 */

import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AuthContext } from "@/App";
import api, { getErrorMessage } from "@/lib/api";
import { programDetailPath, programClassPath } from "@/lib/programsRoute";
import { Card, Eyebrow, Tag, Button, SegmentedControl } from "@/components/studio/primitives";

/** "Sep 14" from an ISO instant, or nothing. */
export function updateDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Where a row opens: the class it is about, else its program. */
export function updateHref(user, row) {
  if (row.class_id) return programClassPath(user, row.program_id, row.class_id);
  return programDetailPath(user, row.program_id);
}

/**
 * The columns for where the list is shown: Program only across programs,
 * Class everywhere but inside a class.
 */
export function updateColumns({ programId = null, classId = null } = {}) {
  return {
    program: !programId && !classId,
    klass: !classId,
  };
}

const TH = "px-4 py-2.5 text-left font-mono text-[11px] font-normal uppercase tracking-wide text-[var(--text-tertiary)]";
const DASH = <span className="text-[var(--text-tertiary)]">—</span>;

export default function UpdatesInbox({ programId = null, classId = null, onUnread, heading = "Updates" }) {
  const { user, refreshUser } = useContext(AuthContext) || {};
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);

  const unread = useMemo(() => rows.filter((r) => !r.read).length, [rows]);

  useEffect(() => {
    onUnread?.(unread);
    // `onUnread` is a setter from the parent; a new function each render must
    // not re-report a count that has not changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unread]);

  // Only the narrowing that applies: a class's list is already one program's.
  const scope = useMemo(
    () => (classId ? { class_id: classId } : programId ? { program_id: programId } : {}),
    [programId, classId],
  );
  const columns = updateColumns({ programId, classId });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/marketplace/updates", { params: scope });
      setRows(res.data?.updates || []);
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't load your updates"));
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    load();
  }, [load]);

  const open = async (row) => {
    if (!row.read) {
      // Optimistic: the row is read the moment it is opened, and the badge
      // follows the server's count once the write lands.
      setRows((list) => list.map((r) => (r.update_id === row.update_id ? { ...r, read: true } : r)));
      api
        .post(`/marketplace/updates/${row.update_id}/read`)
        .then(() => refreshUser?.())
        .catch(() => {});
    }
    navigate(updateHref(user, row));
  };

  const markAll = async () => {
    setBusy(true);
    try {
      await api.post("/marketplace/updates/read-all", null, { params: scope });
      setRows((list) => list.map((r) => ({ ...r, read: true })));
      refreshUser?.();
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't mark them read"));
    } finally {
      setBusy(false);
    }
  };

  const visible = filter === "unread" ? rows.filter((r) => !r.read) : rows;

  return (
    <Card padding={0} data-testid="program-updates">
      <div className="flex flex-wrap items-center justify-between gap-3 px-[18px] pb-3 pt-4">
        <Eyebrow>{heading}</Eyebrow>
        <div className="flex flex-wrap items-center gap-2">
          <SegmentedControl
            testId="updates-filter"
            value={filter}
            onChange={setFilter}
            options={[
              { id: "all", label: "All", testId: "updates-all" },
              { id: "unread", label: "Unread", count: unread || undefined, testId: "updates-unread" },
            ]}
          />
          {unread > 0 && (
            <Button
              variant="ghost"
              onPress={markAll}
              disabled={busy}
              className="whitespace-nowrap"
              data-testid="updates-mark-all"
            >
              Mark all as read
            </Button>
          )}
        </div>
      </div>
      {loading ? (
        <p className="px-[18px] pb-6 pt-2 font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</p>
      ) : visible.length === 0 ? (
        <div className="px-[18px] pb-6 pt-2 font-sans text-[15px] text-[var(--text-tertiary)]" data-testid="program-updates-empty">
          {rows.length === 0 ? "Nothing has changed since you joined." : "You're up to date."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[36rem] border-collapse text-left" data-testid="updates-table">
            <thead>
              <tr className="border-y border-[var(--studio-divider)]">
                <th scope="col" className={TH}>Update</th>
                {columns.program && <th scope="col" className={TH} data-testid="updates-col-program">Program</th>}
                {columns.klass && <th scope="col" className={TH} data-testid="updates-col-class">Class</th>}
                <th scope="col" className={TH}>From</th>
                <th scope="col" className={`${TH} w-[1%] whitespace-nowrap`}>Date</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((u, i) => (
                <tr
                  key={u.update_id}
                  data-testid={`update-row-${u.update_id}`}
                  onClick={() => open(u)}
                  className={[
                    "cursor-pointer align-top transition-colors duration-150 hover:bg-[var(--studio-overlay)]",
                    i < visible.length - 1 ? "border-b border-[var(--studio-divider)]" : "",
                  ].join(" ")}
                >
                  <td className="px-4 py-3">
                    {/* The row is the target; the button is how a keyboard gets there. */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        open(u);
                      }}
                      className="flex w-full items-start gap-2 text-left font-sans text-[15px] leading-snug text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-yellow)]"
                    >
                      <span className="min-w-0 whitespace-pre-line break-words">{u.summary}</span>
                      {!u.read && <Tag accent className="shrink-0">New</Tag>}
                    </button>
                  </td>
                  {columns.program && (
                    <td className="px-4 py-3 font-sans text-[14px] text-[var(--text-secondary)]">{u.program_title || DASH}</td>
                  )}
                  {columns.klass && (
                    <td className="px-4 py-3 font-sans text-[14px] text-[var(--text-secondary)]">{u.class_title || DASH}</td>
                  )}
                  <td className="px-4 py-3 font-sans text-[14px] text-[var(--text-secondary)]" data-testid={`update-from-${u.update_id}`}>
                    {u.sender_name || u.org_name ? (
                      <>
                        <div className="text-[var(--text-primary)]">{u.sender_name || u.org_name}</div>
                        {u.sender_name && u.org_name && (
                          <div className="text-[13px] text-[var(--text-tertiary)]">{u.org_name}</div>
                        )}
                      </>
                    ) : (
                      DASH
                    )}
                  </td>
                  <td className="w-[1%] whitespace-nowrap px-4 py-3 font-mono text-[13px] text-[var(--text-tertiary)]">
                    {updateDate(u.at) || DASH}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
